import { useIntl } from "react-intl";
import { AppShell, Callout, DropdownField, FullWidthPage, useMediaQuery } from "@wayflyer/flyui";
import { NoTabContainer, PanelContainer, PanelPadding } from "@wayflyer/flyui-dataviz";

import {
  ChartContainer,
  DataGroupContainer,
  LtvCacFilterContainer,
  LtvCacFiltersContainer,
} from "../../../../components/styled";
import { LtvCac } from "../chart";

import type { LtvCacPeriod, LtvCacScreenProps, LtvCacChartType } from "./interface";

/**
 * LtvCac component
 */
export const LtvCacScreen = ({
  chartDataLoading = false,
  chartDataError = "",
  mainChart,
  pageTitle,
  chartTypes,
  customerChart,
  selectedChartType = "ltv",
  customerTitle,
  selectedLtvPeriod,
  selectedAovPeriod,
  selectedFreqPeriod,
  periods,
  onChangeChart,
  onChangePeriod,
}: LtvCacScreenProps): UIElement => {
  const query = useMediaQuery();
  const intl = useIntl();

  return (
    <AppShell>
      <FullWidthPage title={pageTitle}>
        <DataGroupContainer>
          <PanelContainer>
            <LtvCacFiltersContainer>
              <LtvCacFilterContainer>
                <DropdownField
                  initialValue={selectedChartType}
                  disabled={false}
                  readonly={false}
                  name="chart-type"
                  options={chartTypes}
                  label={intl.formatMessage({
                    id: "Internal LTV / CAC: LTV/AOV/FREQUENCY",
                    defaultMessage: "LTV/AOV/FREQUENCY",
                  })}
                  onChange={(id) => onChangeChart(id as LtvCacChartType)}
                />
              </LtvCacFilterContainer>
              <LtvCacFilterContainer>
                <DropdownField
                  initialValue={selectedLtvPeriod}
                  disabled={selectedChartType !== "ltv"}
                  readonly={false}
                  name="ltv-period"
                  options={periods}
                  label={intl.formatMessage({ id: "Internal LTV / CAC: LTV SELECTOR", defaultMessage: "LTV SELECTOR" })}
                  onChange={(id) => onChangePeriod(selectedChartType, id as LtvCacPeriod)}
                />
              </LtvCacFilterContainer>
              <LtvCacFilterContainer>
                <DropdownField
                  initialValue={selectedAovPeriod}
                  disabled={selectedChartType !== "aov"}
                  readonly={false}
                  name="aov-period"
                  options={periods}
                  label={intl.formatMessage({ id: "Internal LTV / CAC: AOV SELECTOR", defaultMessage: "AOV SELECTOR" })}
                  onChange={(id) => onChangePeriod(selectedChartType, id as LtvCacPeriod)}
                />
              </LtvCacFilterContainer>
              <LtvCacFilterContainer>
                <DropdownField
                  initialValue={selectedFreqPeriod}
                  disabled={selectedChartType !== "frequency"}
                  readonly={false}
                  name="freq-period"
                  options={periods}
                  label={intl.formatMessage({
                    id: "Internal LTV / CAC: FREQUENCY SELECTOR",
                    defaultMessage: "FREQUENCY SELECTOR",
                  })}
                  onChange={(id) => onChangePeriod(selectedChartType, id as LtvCacPeriod)}
                />
              </LtvCacFilterContainer>
            </LtvCacFiltersContainer>
            <PanelPadding>
              {chartDataError ? <Callout variant="critical" description={chartDataError} /> : null}
              {!chartDataError && query("sm").windowGt && mainChart ? (
                <LtvCac {...mainChart} loading={chartDataLoading} />
              ) : null}
              {!chartDataError && query("sm").windowLte && mainChart && (
                <NoTabContainer>
                  <ChartContainer>
                    <LtvCac {...mainChart} loading={chartDataLoading} />
                  </ChartContainer>
                </NoTabContainer>
              )}
              {customerTitle ? <h3>{customerTitle}</h3> : null}
              {!chartDataError && query("sm").windowGt && customerChart ? (
                <LtvCac {...customerChart} loading={chartDataLoading} isCustomer={true} />
              ) : null}
              {!chartDataError && query("sm").windowLte && customerChart && (
                <NoTabContainer>
                  <ChartContainer>
                    <LtvCac {...customerChart} loading={chartDataLoading} isCustomer={true} />
                  </ChartContainer>
                </NoTabContainer>
              )}
            </PanelPadding>
          </PanelContainer>
        </DataGroupContainer>
      </FullWidthPage>
    </AppShell>
  );
};
